// API utilities with automatic token refresh

type TokenRefreshCallback = (newAccessToken: string, newRefreshToken: string) => void;
type LogoutCallback = () => void;

let tokenRefreshCallback: TokenRefreshCallback | null = null;
let logoutCallback: LogoutCallback | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// In-memory token store (survives localStorage being cleared)
let memoryAccessToken: string | null = null;
let memoryRefreshToken: string | null = null;

// Set tokens from login/refresh - stores in both memory and localStorage
export const setAuthTokens = (accessToken: string | null, refreshToken: string | null) => {
  if (accessToken) {
    memoryAccessToken = accessToken;
    localStorage.setItem('accessToken', accessToken);
  }
  if (refreshToken) {
    memoryRefreshToken = refreshToken;
    localStorage.setItem('refreshToken', refreshToken);
  }
};

// Clear tokens on logout
export const clearAuthTokens = () => {
  memoryAccessToken = null;
  memoryRefreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

// Get the best available token (memory first, then localStorage)
const getAccessToken = (): string | null => {
  return memoryAccessToken || localStorage.getItem('accessToken');
};

const getRefreshToken = (): string | null => {
  return memoryRefreshToken || localStorage.getItem('refreshToken');
};

export const setTokenCallbacks = (
  onRefresh: TokenRefreshCallback,
  onLogout: LogoutCallback
) => {
  tokenRefreshCallback = onRefresh;
  logoutCallback = onLogout;
};

const refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch('https://api.volt.bz/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // API may return { success, data: { accessToken, refreshToken } }
        const tokenData = data.data || data;
        const newAccessToken = tokenData.accessToken || tokenData.access_token || tokenData.token;
        const newRefreshToken = tokenData.refreshToken || tokenData.refresh_token;
        
        // Update stored tokens only if we got valid values
        setAuthTokens(newAccessToken, newRefreshToken);
        
        // Notify the app of new tokens
        if (tokenRefreshCallback) {
          tokenRefreshCallback(newAccessToken, newRefreshToken || refreshToken);
        }
        
        return newAccessToken;
      } else {
        // Refresh token is also expired, logout
        if (logoutCallback) {
          logoutCallback();
        }
        return null;
      }
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

interface ApiOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
}

export const apiRequest = async (
  url: string,
  options: ApiOptions = {}
): Promise<Response> => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  const makeRequest = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  };

  // Make initial request
  let response = await makeRequest(accessToken);

  // If we get 401 and have a refresh token, try to refresh
  if (response.status === 401 && refreshToken) {
    const newAccessToken = await refreshAccessToken(refreshToken);
    
    if (newAccessToken) {
      // Retry the request with new token
      response = await makeRequest(newAccessToken);
    }
  }

  return response;
};

// Convenience methods
export const api = {
  get: (url: string, options?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiRequest(url, { ...options, method: 'GET' }),

  post: (url: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiRequest(url, { ...options, method: 'POST', body }),

  put: (url: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiRequest(url, { ...options, method: 'PUT', body }),

  delete: (url: string, options?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiRequest(url, { ...options, method: 'DELETE' }),
};
